import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Building2,
  Inbox,
  Plus,
  BookOpen,
  ChevronLeft,
  LogOut,
  ChevronDown,
  ChevronRight,
  Folder,
  HelpCircle,
  Mail,
  LifeBuoy,
  KeyRound,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { sd } from '@sd/lib/sdPaths';
import { isExternalSdRole } from '@sd/lib/sdRoles';
import { useCurrentUserName } from '@sd/hooks/useCurrentUserName';
import { ResetPasswordDialog } from '@sd/components/common/ResetPasswordDialog';

interface CustomerSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  onNewTicket?: () => void;
}

const kbSubCategories = [
  { id: 'all', label: 'All Articles', icon: BookOpen },
  { id: 'getting-started', label: 'Getting Started', icon: Folder },
  { id: 'troubleshooting', label: 'Troubleshooting', icon: Folder },
  { id: 'how-to', label: 'How-To Guides', icon: Folder },
  { id: 'security', label: 'Security', icon: Folder },
  { id: 'software', label: 'Software', icon: Folder },
  { id: 'policies', label: 'IT Policies', icon: Folder },
];

export function CustomerSidebar({ collapsed, onToggle, onNewTicket }: CustomerSidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut, role } = useAuth();
  const { displayName, initials } = useCurrentUserName();

  const isOnKB = location.pathname.startsWith(sd('knowledge-base'));
  const [kbOpen, setKbOpen] = useState(isOnKB);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);

  useEffect(() => {
    if (isOnKB) setKbOpen(true);
  }, [isOnKB]);

  const params = new URLSearchParams(location.search);
  const activeCat = params.get('cat') || 'all';

  const mainItems = [
    {
      label: 'My Tickets',
      icon: Inbox,
      onClick: () => navigate(sd('customer')),
      active: location.pathname.startsWith(sd('customer')),
    },
  ];

  const bottomItems = [
    {
      label: 'FAQs',
      icon: HelpCircle,
      onClick: () => navigate(`${sd('knowledge-base')}?cat=how-to`),
      active: false,
    },
    {
      label: 'Contact Support',
      icon: Mail,
      onClick: () => onNewTicket?.(),
      active: false,
    },
  ];

  const goToKB = (catId: string) => {
    navigate(`${sd("knowledge-base")}?cat=${catId}`);
  };

  return (
    <aside
      className={cn(
        'h-full bg-sidebar border-r-2 border-sidebar-border flex flex-col transition-all duration-300',
        collapsed ? 'w-14' : 'w-60'
      )}
    >
      {/* Header */}
      <div className="h-14 px-3 flex items-center justify-between border-b border-sidebar-border">
        {!collapsed && (
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-md bg-primary flex items-center justify-center">
              <LifeBuoy className="h-4 w-4 text-primary-foreground" />
            </div>
            <div>
              <p className="text-xs font-bold text-sidebar-foreground">Customer</p>
              <p className="text-[10px] text-muted-foreground -mt-0.5">Support Portal</p>
            </div>
          </div>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggle}
          className="h-8 w-8 text-sidebar-foreground hover:bg-sidebar-accent"
        >
          <ChevronLeft className={cn('h-4 w-4 transition-transform', collapsed && 'rotate-180')} />
        </Button>
      </div>

      {/* User profile card — top */}
      <div className={cn(
        'shrink-0 border-b border-sidebar-border bg-sidebar-accent/20 px-3 py-3',
        collapsed ? 'flex justify-center' : ''
      )}>
        {collapsed ? (
          <div
            title={displayName ?? 'User'}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground"
          >
            {initials}
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-sidebar-foreground leading-tight">
                {displayName ?? 'User'}
              </p>
              <span className="inline-block rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary leading-tight">
                End User
              </span>
            </div>
          </div>
        )}
      </div>

      {/* New Ticket CTA */}
      {onNewTicket && (
        <div className="p-2 border-b border-sidebar-border">
          <button
            onClick={onNewTicket}
            className={cn(
              'w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors',
              collapsed && 'justify-center px-0'
            )}
            title="New Ticket"
          >
            <Plus className="h-4 w-4 flex-shrink-0" />
            {!collapsed && <span>New Ticket</span>}
          </button>
        </div>
      )}

      {/* Main nav */}
      <div className="flex-1 overflow-y-auto py-2 px-2 space-y-0.5">
        {!collapsed && (
          <p className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Main
          </p>
        )}
        {mainItems.map((item) => (
          <button
            key={item.label}
            onClick={item.onClick}
            className={cn(
              'w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors',
              item.active
                ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                : 'text-sidebar-foreground hover:bg-sidebar-accent/50',
              collapsed && 'justify-center px-0'
            )}
            title={collapsed ? item.label : undefined}
          >
            <item.icon className="h-4 w-4 flex-shrink-0" />
            {!collapsed && <span>{item.label}</span>}
          </button>
        ))}

        {/* Knowledge Base — expandable */}
        <div className="pt-1">
          <button
            onClick={() => {
              if (collapsed) {
                navigate(sd('knowledge-base'));
              } else {
                setKbOpen((v) => !v);
              }
            }}
            className={cn(
              'w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors',
              isOnKB
                ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                : 'text-sidebar-foreground hover:bg-sidebar-accent/50',
              collapsed && 'justify-center px-0'
            )}
            title={collapsed ? 'Knowledge Base' : undefined}
          >
            <BookOpen className="h-4 w-4 flex-shrink-0" />
            {!collapsed && (
              <>
                <span className="flex-1 text-left">Knowledge Base</span>
                {kbOpen ? (
                  <ChevronDown className="h-3.5 w-3.5" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5" />
                )}
              </>
            )}
          </button>

          {/* KB Sub-categories */}
          {!collapsed && kbOpen && (
            <div className="ml-3 mt-1 pl-3 border-l border-sidebar-border space-y-0.5">
              {kbSubCategories.map((sub) => {
                const isActive = isOnKB && activeCat === sub.id;
                return (
                  <button
                    key={sub.id}
                    onClick={() => goToKB(sub.id)}
                    className={cn(
                      'w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs transition-colors',
                      isActive
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-muted-foreground hover:bg-sidebar-accent/50 hover:text-sidebar-foreground'
                    )}
                  >
                    <sub.icon className="h-3.5 w-3.5 flex-shrink-0" />
                    <span className="flex-1 text-left">{sub.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Help group */}
        {!collapsed && (
          <p className="px-2 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Help
          </p>
        )}
        {bottomItems.map((item) => (
          <button
            key={item.label}
            onClick={item.onClick}
            className={cn(
              'w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors text-sidebar-foreground hover:bg-sidebar-accent/50',
              collapsed && 'justify-center px-0'
            )}
            title={collapsed ? item.label : undefined}
          >
            <item.icon className="h-4 w-4 flex-shrink-0" />
            {!collapsed && <span>{item.label}</span>}
          </button>
        ))}
      </div>

      {/* Logout — Customer / end-user panel */}
      <div className="border-t border-sidebar-border p-2 space-y-1">
        {isExternalSdRole(role) ? (
          <button
            type="button"
            onClick={() => setResetPasswordOpen(true)}
            className={cn(
              "w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors text-sidebar-foreground hover:bg-sidebar-accent/50",
              collapsed && "justify-center px-0"
            )}
            title="Reset Password"
          >
            <KeyRound className="h-4 w-4 flex-shrink-0" />
            {!collapsed && <span>Reset Password</span>}
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => {
            void (async () => {
              await signOut();
              navigate(sd("login"), { replace: true });
            })();
          }}
          className={cn(
            "w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors text-destructive hover:bg-destructive/10",
            collapsed && "justify-center px-0"
          )}
          title="Logout"
        >
          <LogOut className="h-4 w-4 flex-shrink-0" />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>
      {isExternalSdRole(role) ? (
        <ResetPasswordDialog
          open={resetPasswordOpen}
          onOpenChange={setResetPasswordOpen}
        />
      ) : null}
    </aside>
  );
}
